import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useErrorStore } from '@/stores/errors';

export type ModelEditionMode = 'edit' | 'create' | 'show' | 'clone';

/** Props every `*EditionForm` built on `ModelEditionForm` takes. */
export interface ModelEditionFormProps {
  modelId?: number;
  mode: ModelEditionMode;
}

/** How a field is locked while the form is read only. */
export type FieldLock = 'readonly' | 'disabled';

/**
 * The state an edition form's fields share: whether the form is read only,
 * and the server's validation errors.
 *
 * `field(name)` gives the props a field needs for both, ready to spread with
 * `v-bind="field('name')"`. The lock only works if the field's component
 * honours it: Vuetify inputs (and components whose root is one) take the
 * default `readonly`; our own inputs that only understand `disabled` -
 * DatePicker, RadioButtonGroup, RichTextEditor, ModelSelectDropdown,
 * PublicationSelect, UserSelect, ShiftPlanDateInput - need
 * `field('name', 'disabled')`.
 *
 * Validating on change is `ModelEditionForm`'s job; a form must not add its
 * own watch for it.
 */
export function useModelEditionForm(props: { mode: ModelEditionMode }) {
  const readOnly = computed(() => props.mode === 'show');
  const { validationErrors } = storeToRefs(useErrorStore());

  function field(name: string, lock: FieldLock = 'readonly') {
    const messages = validationErrors.value[name];

    return {
      'error': !!messages,
      'error-messages': messages,
      [lock]: readOnly.value,
    };
  }

  return { readOnly, validationErrors, field };
}
